// The dashboard nav tree for the signed-in user. A thin wrapper over the old rail's hooks so the new
// sidebar shows exactly what the old one did: the menu tree, the role / email / view-only / billing /
// professional-mode filters, custom-role permissions (usePermissions().canAccessMenu) and per-company
// module locks all come from useSidebarIdentity + useSidebarMenus in sidebar-v2.tsx, not a copy.
import { useMemo, type ReactElement } from 'react';

import { useSidebarIdentity, useSidebarMenus } from '@components/sidebar/sidebar-v2';
import { MODULE_BY_SIDEBAR_LABEL } from '@constants/modules';

export interface NavLinkEntry {
  /** Untranslated label: the tour anchors, the module lookup and the lock dialog key off it. */
  label: string;
  i18nKey?: string;
  icon: ReactElement;
  link: string;
  /** Untranslated badge text (BOD, New, Mulai, AI Ready); translate with BADGE_KEYS. */
  badge?: string;
}

export interface NavEntry extends NavLinkEntry {
  /** Present on groups (link `#`). */
  subLinks?: NavLinkEntry[];
  /** Module disabled for this company: show the row dimmed and open the lock dialog instead. */
  locked: boolean;
}

/** Badge text → translation key; unknown badges show as they are (same map as the old rail). */
export const BADGE_KEYS: Record<string, string> = {
  BOD: 'badge.bod',
  New: 'badge.new',
  Mulai: 'badge.mulai',
  'AI Ready': 'badge.aiReady',
};

export function useNavTree() {
  const { userEmail, professionalModeEnabled } = useSidebarIdentity();
  const { filteredMenus, isFeatureEnabled } = useSidebarMenus({ userEmail, professionalModeEnabled });

  const items = useMemo<NavEntry[]>(
    () =>
      filteredMenus.map((item) => {
        const moduleCode = MODULE_BY_SIDEBAR_LABEL[item.label];
        return {
          label: item.label,
          i18nKey: item.i18nKey,
          icon: item.icon,
          link: item.link,
          badge: item.badge,
          subLinks: item.subLinks?.map((sub) => ({
            label: sub.label,
            i18nKey: sub.i18nKey,
            icon: sub.icon,
            link: sub.link,
            badge: sub.badge,
          })),
          locked: moduleCode ? !isFeatureEnabled(moduleCode) : false,
        };
      }),
    [filteredMenus, isFeatureEnabled]
  );

  // The old rail's header showed the Gateway partner logo for these accounts.
  const isGatewayUser = userEmail.toLowerCase().includes('gateway');

  return { items, isGatewayUser };
}
